import React, { useEffect, useState } from 'react';
import AdminRepository from '../../services/AdminRepository.js';
import LoadingSpinner from '../common/LoadingSpinner.jsx';
import { AdminFormPageShimmer } from '../common/Shimmer.jsx';
import { devError } from '../../utils/logger.js';
import { useAdminDialog } from '../../contexts/AdminDialogContext.jsx';
import { useScreenProbe } from '../../utils/screenTiming.js';
import './AdminClinicList.css';

/**
 * ロール設定画面（ユーザー追加時に選択可能なロールの管理）
 */
const AdminRoleSettings = ({ onUpdate }) => {
  const adminRepo = AdminRepository.getInstance();
  const { showAlert } = useAdminDialog();
  const probe = useScreenProbe('roles');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [roles, setRoles] = useState([]);
  const [saveError, setSaveError] = useState('');

  const loadRoles = async () => {
    probe.start('load');
    setLoading(true);
    setSaveError('');
    try {
      const list = await adminRepo.getRoleList();
      setRoles(Array.isArray(list) ? list.map((r) => ({ ...r })) : []);
      probe.end('load', 'ok');
      probe.unlock();
    } catch (err) {
      probe.end('load', `error: ${err?.message || 'unknown'}`);
      devError('ロール一覧取得エラー:', err);
      setSaveError('ロール一覧の取得に失敗しました。');
      setRoles([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadRoles();
  }, []);

  const handleChange = (index, field, value) => {
    setRoles((prev) => {
      const next = prev.map((r, i) => (i === index ? { ...r, [field]: value } : r));
      return next;
    });
    setSaveError('');
  };

  const handleAdd = () => {
    setRoles((prev) => [...prev, { label: '', value: '' }]);
    setSaveError('');
  };

  const handleRemove = (index) => {
    if (roles.length <= 1) {
      setSaveError('ロールは1件以上必要です。');
      return;
    }
    setRoles((prev) => prev.filter((_, i) => i !== index));
    setSaveError('');
  };

  const handleSave = async (e) => {
    e.preventDefault();
    const normalized = roles.map((r) => ({
      label: String(r.label || '').trim() || r.value,
      value: String(r.value || '').trim().toLowerCase().replace(/\s+/g, '_')
    }));
    const emptyLabel = normalized.find((r) => !r.label);
    const emptyValue = normalized.find((r) => !r.value);
    if (emptyLabel || emptyValue) {
      setSaveError('表示名とDB登録名称はすべて入力してください。');
      return;
    }
    const values = normalized.map((r) => r.value);
    if (new Set(values).size !== values.length) {
      setSaveError('DB登録名称は重複できません。');
      return;
    }
    setSaving(true);
    setSaveError('');
    try {
      await adminRepo.updateRoleList(normalized);
      await loadRoles();
      if (onUpdate) onUpdate();
      await showAlert({ message: 'ロール設定を更新しました。', variant: 'success', title: '保存完了' });
    } catch (err) {
      setSaveError(err.message || 'ロールの更新に失敗しました。');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="admin-list-container">
      <div className="list-header">
        <div className="section-header">
          <h1>ロール設定</h1>
          <p>ユーザー追加時に選択可能なロールを管理します。表示名は画面表示用、DB登録名称はFirestoreに保存する値です。</p>
        </div>
        <button onClick={loadRoles} className="btn btn-secondary" disabled={loading}>
          再読み込み
        </button>
      </div>

      {loading ? (
        <AdminFormPageShimmer fields={4} withHeader={false} />
      ) : (
        <form onSubmit={handleSave} className="modal-body">
          <div className="form-group">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
              <label style={{ marginBottom: 0 }}>ロール一覧</label>
              <button type="button" onClick={handleAdd} className="btn btn-sm btn-primary">
                追加
              </button>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              {roles.map((role, index) => (
                <div
                  key={index}
                  style={{
                    display: 'flex',
                    gap: '12px',
                    alignItems: 'center',
                    flexWrap: 'wrap'
                  }}
                >
                  <input
                    type="text"
                    value={role.label}
                    onChange={(e) => handleChange(index, 'label', e.target.value)}
                    placeholder="表示名（例: 歯科医師）"
                    style={{ flex: '1 1 140px', minWidth: 0 }}
                  />
                  <input
                    type="text"
                    value={role.value}
                    onChange={(e) => handleChange(index, 'value', e.target.value)}
                    placeholder="DB登録名称（例: doctor）"
                    style={{ flex: '1 1 140px', minWidth: 0 }}
                  />
                  <button
                    type="button"
                    onClick={() => handleRemove(index)}
                    className="btn btn-sm btn-danger"
                    disabled={roles.length <= 1}
                    title="削除"
                  >
                    削除
                  </button>
                </div>
              ))}
            </div>
            <small style={{ color: 'var(--color-text-muted)', display: 'block', marginTop: '8px' }}>
              DB登録名称は英小文字・数字・アンダースコアのみ使用できます。保存時に自動で整形されます。
            </small>
          </div>
          {saveError && (
            <div style={{ color: 'var(--color-danger)', marginBottom: '12px' }}>{saveError}</div>
          )}
          <div className="modal-footer" style={{ borderTop: 'none', paddingTop: '16px' }}>
            <button type="submit" className="btn btn-primary" disabled={saving}>
              {saving ? <LoadingSpinner size="small" /> : '保存'}
            </button>
          </div>
        </form>
      )}
    </div>
  );
};

export default AdminRoleSettings;
